import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { eventsAPI, membersAPI, unwrapList } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useAuth } from '../../context/AuthContext';
import './Pos.css';

const regLabel = {
  confirmed: '已確認',
  pending: '待確認',
  cancelled: '已取消',
};

const EventParticipants = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const volunteer = user?.desk_role === 'volunteer';
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [eventRes, regRes, attRes] = await Promise.all([
      eventsAPI.adminGet(id),
      eventsAPI.registrations(id),
      eventsAPI.attendances(id),
    ]);
    setEvent(eventRes.data);
    setRegistrations(unwrapList(regRes.data));
    setAttendances(unwrapList(attRes.data).filter((row) => row.status !== 'absent'));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch(() => {
        if (!cancelled) setMessage('找不到活動');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const registerQuery = async () => {
    const value = query.trim();
    if (!value) return;
    setMessage('');
    try {
      const found = await membersAPI.lookup(value);
      const match = found.data;
      if (match.match !== 'one' || !match.results?.[0]) {
        setMessage(match.match === 'many' ? '多於一位會員，請改用完整編號' : '找不到會員');
        return;
      }
      const response = await eventsAPI.registerMember(id, match.results[0].id);
      setMessage(response.data.message || '報名完成');
      setQuery('');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || err.response?.data?.error || '報名失敗');
    }
  };

  const checkInMember = async (memberNumber) => {
    setMessage('');
    try {
      const response = await eventsAPI.checkIn({
        q: memberNumber,
        event_id: id,
        register_and_check_in: false,
      });
      setMessage(response.data.message || '簽到完成');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || err.response?.data?.error || '簽到失敗');
    }
  };

  const removeRegistration = async (registrationId) => {
    if (!window.confirm('確認刪除？')) return;
    setMessage('');
    try {
      await eventsAPI.deleteRegistration(registrationId);
      setMessage('已刪除報名');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || '刪除報名失敗');
    }
  };

  const removeAttendance = async (attendanceId) => {
    if (!window.confirm('確認刪除？')) return;
    setMessage('');
    try {
      await eventsAPI.deleteAttendance(attendanceId);
      setMessage('已刪除簽到');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || '刪除簽到失敗');
    }
  };

  if (loading) return <LoadingPage message="載入參加者..." />;
  if (!event) return <div className="pos-error">{message || '找不到活動'}</div>;

  return (
    <div className="pos-card">
      <p>
        <Link to="/admin/events">返回活動</Link>
        {!volunteer && (
          <>
            {' · '}
            <Link to={`/admin/events/${id}/edit`}>編輯活動</Link>
          </>
        )}
      </p>
      <h1>{event.title}</h1>
      <p className="pos-hint">報名參加者，或為已報名會員簽到。</p>

      <section className="pos-section">
        <h2>報名參加者</h2>
        <input
          className="pos-scan-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="會員編號、電話或姓名"
          aria-label="報名參加者"
          onKeyDown={(e) => {
            if (e.key === 'Enter') registerQuery();
          }}
        />
        <div className="pos-actions">
          <button type="button" className="pos-btn" onClick={registerQuery}>報名參加者</button>
        </div>
        {message && <p className="pos-hint">{message}</p>}
      </section>

      <section className="pos-section">
        <h2>參加者</h2>
        {registrations.length === 0 ? (
          <p>暫無報名</p>
        ) : (
          <ul className="pos-list">
            {registrations.map((row) => {
              const member = row.member_details || {};
              const attendance = attendances.find((item) => item.member === row.member);
              return (
                <li key={row.id}>
                  <div>{member.chinese_name || '會員'} · {member.member_number || row.member}</div>
                  <div>{regLabel[row.status] || row.status}{attendance ? ' · 已簽到' : ''}</div>
                  {row.status === 'confirmed' && !attendance && member.member_number && (
                    <button type="button" className="pos-btn" onClick={() => checkInMember(member.member_number)}>
                      簽到參加者
                    </button>
                  )}
                  {!volunteer && (
                    <div className="pos-actions">
                      <button type="button" className="pos-btn secondary" onClick={() => removeRegistration(row.id)}>刪除報名</button>
                      {attendance && (
                        <button type="button" className="pos-btn secondary" onClick={() => removeAttendance(attendance.id)}>刪除簽到</button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default EventParticipants;
